"use client";
import React, { useState } from "react";
import {
  resolveMemberId,
  getMemberCombinedPrice,
  getMemberCombinedDuration,
  applyMemberPricing,
  resolveRequirementLabel,
  resolveSelectionPaymentRequirement,
  formatPrice,
  formatDuration,
} from "@openings/booking-core";
import type { LocationOpeningsItem, DbTimeString } from "@openings/api-client";
import { formatCompactTime } from "@utils/core";
import { MemberPanelShell } from "../MemberPanelShell";
import type { BookingOpeningsClient, SelectedService } from "../types";
import { memberInfoBtnStyle } from "../bookingStyles";
import { TimeGrid } from "../components/TimeGrid";
import { MemberInfoModal } from "./MemberInfoPanel";
import { ConsultationButton } from "./ConsultationButton";

const InfoCircleIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="64 64 896 896"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
    <path d="M464 336a48 48 0 1096 0 48 48 0 10-96 0zm24 112h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V456c0-4.4 3.6-8 8-8z" />
  </svg>
);

/**
 * Single member panel in schedule-mode booking. Shows the member's name,
 * photo, service/pricing meta, time slots (or consultation button), and
 * an expandable portfolio section.
 */

/**
 * "Aug 24" / "Aug 24, 2027" — the end of a vacation range, in the customer's
 * locale. Parsed as a local date: the value is a plain YYYY-MM-DD calendar
 * day, and `new Date("2026-08-24")` would be read as UTC midnight and can
 * render as the day before for anyone west of Greenwich.
 */
function formatVacationEnd(endDate: string): string {
  const [year, month, day] = endDate.split("-").map(Number);
  if (!year || !month || !day) return endDate;
  const date = new Date(year, month - 1, day);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function MemberPanel({
  member,
  salonId,
  selectedServices,
  isConsultation,
  isSingleService,
  onSlotClick,
  onConsultationClick,
  openingsClient,
  showAvailabilityStatus = false,
  selectionResetKey,
  selectedTime,
  onSelectedTimeChange,
  memberPageHref,
}: {
  member: LocationOpeningsItem;
  salonId?: string;
  selectedServices: SelectedService[];
  isConsultation: boolean;
  isSingleService: boolean;
  onSlotClick: (member: LocationOpeningsItem, time: DbTimeString) => void;
  onConsultationClick: (member: LocationOpeningsItem) => void;
  openingsClient: BookingOpeningsClient;
  showAvailabilityStatus?: boolean;
  selectionResetKey?: string;
  selectedTime?: DbTimeString | null;
  onSelectedTimeChange?: (time: DbTimeString) => void;
  memberPageHref?: string;
}) {
  const [showInfo, setShowInfo] = useState(false);
  const memberKey = resolveMemberId(member);
  const hasSlots = member.openings.length > 0;
  // A member on vacation comes back with no openings (the API includes them
  // only when `includeVacation` is set). Saying "no times available" for that
  // reads as "fully booked" — the customer needs to know they are away, and
  // until when, so they stop retrying nearby dates.
  const vacationRange = member.dayStatus === "vacation" ? member.vacationRange : null;
  const isOnVacation = member.dayStatus === "vacation";
  const isAllBooked = !hasSlots && !isConsultation && !isOnVacation;

  const vacationLabel = isOnVacation
    ? vacationRange
      ? `Away until ${formatVacationEnd(vacationRange.endDate)}`
      : "Away"
    : null;

  const primaryService = selectedServices[0];
  const memberSvc = primaryService
    ? member.services.find(
        (s) =>
          s.referenceId === primaryService.id || s.id === primaryService.id,
      )
    : undefined;

  const combinedPrice = getMemberCombinedPrice(
    selectedServices,
    member.services,
  );
  const combinedDuration = getMemberCombinedDuration(
    selectedServices,
    member.services,
  );

  // This member's exact effective terms for the current selection. The
  // openings payload carries each clone's inherited payment policy, so the
  // row can state it before a time is picked instead of leaving the customer
  // to discover a charge at the payment step (BF-DRIFT-006). The request
  // (consultation) requirement precedes payment.
  const requirementLabel = resolveRequirementLabel({
    requestRequired: isConsultation,
    paymentRequirement: resolveSelectionPaymentRequirement(
      applyMemberPricing(selectedServices, member.services),
    ),
  });

  const serviceMeta = isSingleService && memberSvc ? (
    <span>
      {[
        typeof memberSvc.price === "number" && memberSvc.price > 0
          ? `${formatPrice(memberSvc.price)}${memberSvc.up ? "+" : ""}`
          : null,
        memberSvc.duration ? formatDuration(memberSvc.duration) : null,
        requirementLabel,
      ]
        .filter(Boolean)
        .join(" · ")}
    </span>
  ) : !isSingleService ? (
    <span>
      {[
        `${selectedServices.length} services`,
        combinedPrice > 0 ? formatPrice(combinedPrice) : null,
        formatDuration(combinedDuration),
        requirementLabel,
      ]
        .filter(Boolean)
        .join(" · ")}
    </span>
  ) : requirementLabel ? (
    <span>{requirementLabel}</span>
  ) : undefined;
  const meta =
    !showAvailabilityStatus && vacationLabel ? (
      <span>{vacationLabel}</span>
    ) : !showAvailabilityStatus && isAllBooked ? (
      <span>No times available</span>
    ) : (
      serviceMeta
    );

  return (
    <MemberPanelShell
      key={memberKey}
      name={member.name}
      nameHref={
        memberPageHref ?? `/member/${encodeURIComponent(memberKey)}`
      }
      photo={member.photo ?? null}
      meta={meta}
      status={
        showAvailabilityStatus && vacationLabel ? (
          <span>{vacationLabel}</span>
        ) : showAvailabilityStatus && isAllBooked ? (
          <span>No times available</span>
        ) : undefined
      }
      dimmed={isAllBooked || isOnVacation}
      hasBookableSlots={!isConsultation && hasSlots}
      showAvailabilityIndicator={showAvailabilityStatus}
      style={
        !showAvailabilityStatus
          ? ({
              "--openings-booking-avatar-size": "36px",
            } as React.CSSProperties)
          : undefined
      }
      trailing={
        showAvailabilityStatus || member.hasPortfolio ? (
          <button
            type="button"
            className="openings-member-info-button"
            style={memberInfoBtnStyle}
            onClick={() => setShowInfo(true)}
            aria-label={`Info about ${member.name}`}
            aria-haspopup="dialog"
          >
            <InfoCircleIcon />
          </button>
        ) : undefined
      }
    >
      <MemberInfoModal
        salonId={salonId}
        member={member}
        selectedServices={showAvailabilityStatus ? selectedServices : []}
        openingsClient={openingsClient}
        open={showInfo}
        onClose={() => setShowInfo(false)}
      />

      {isConsultation ? (
        <ConsultationButton onClick={() => onConsultationClick(member)} />
      ) : hasSlots ? (
        <TimeGrid
          times={member.openings as DbTimeString[]}
          resetKey={`${memberKey}:${selectionResetKey ?? ""}`}
          selectionEnabled={showAvailabilityStatus}
          selectedTime={selectedTime}
          onSelectedTimeChange={onSelectedTimeChange}
          getLabel={(time) =>
            member.openingLabels?.[time] ??
            formatCompactTime(time, {
              uppercase: true,
              alwaysShowMinutes: true,
            })
          }
          onSelect={(time) => onSlotClick(member, time)}
        />
      ) : null}
    </MemberPanelShell>
  );
}
